import { apiFetch } from './client';

export const RETURN_STATUS = {
  REQUESTED: 'REQUESTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  PICKUP_SCHEDULED: 'PICKUP_SCHEDULED',
  PICKUP_COMPLETED: 'PICKUP_COMPLETED',
  PICKUP_FAILED: 'PICKUP_FAILED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
};

export const RETURN_TYPE = {
  RETURN_CREDIT: 'RETURN_CREDIT',
  EXCHANGE: 'EXCHANGE',
};

export const RETURN_TYPE_LABELS = {
  [RETURN_TYPE.RETURN_CREDIT]: 'Return',
  [RETURN_TYPE.EXCHANGE]: 'Exchange',
};

export const RETURN_STATUS_LABELS = {
  [RETURN_STATUS.REQUESTED]: 'Pending Review',
  [RETURN_STATUS.APPROVED]: 'Approved',
  [RETURN_STATUS.REJECTED]: 'Rejected',
  [RETURN_STATUS.PICKUP_SCHEDULED]: 'Pickup Scheduled',
  [RETURN_STATUS.PICKUP_COMPLETED]: 'In-Store Verification',
  [RETURN_STATUS.PICKUP_FAILED]: 'Pickup Failed',
  [RETURN_STATUS.COMPLETED]: 'Completed',
  [RETURN_STATUS.CANCELLED]: 'Cancelled',
};

/** order_channel on the RMA — SUBS = subscription drop, DELIVERY = grocery/other. */
export const ORDER_CHANNEL = {
  DELIVERY: 'DELIVERY',
  SUBS: 'SUBS',
};

export const ORDER_CHANNEL_LABELS = {
  [ORDER_CHANNEL.DELIVERY]: 'Grocery',
  [ORDER_CHANNEL.SUBS]: 'Subscription',
};

/** Set when Ops completes via dispose (no pickup). */
export const RETURN_RESOLUTION = {
  CUSTOMER_DISPOSE: 'CUSTOMER_DISPOSE',
};

export const RETURN_RESOLUTION_LABELS = {
  [RETURN_RESOLUTION.CUSTOMER_DISPOSE]: 'Customer dispose',
};

export const RETURN_ERRORS = {
  dispose_credit_only: 'Dispose & credit is only for store-credit returns (not exchanges).',
  evidence_required: 'Customer must upload at least one photo before dispose & credit.',
  invalid_status: 'This return cannot be disposed in its current status.',
  reason_required: 'A reason is required.',
  delivery_person_sub_required: 'Select a delivery rider.',
};

function normalizeOrderChannel(raw) {
  const explicit = String(
    raw?.orderChannel ?? raw?.order_channel ?? raw?.channel ?? '',
  ).trim().toUpperCase();
  // Handoff: filter on order_channel only — do not split on reason codes.
  return explicit === ORDER_CHANNEL.SUBS ? ORDER_CHANNEL.SUBS : ORDER_CHANNEL.DELIVERY;
}

// In-memory mock store for fallback operations
let mockReturnRequests = [
  {
    id: 'RET-1001',
    orderId: 'ORD-8821',
    customerName: 'Rahul Verma',
    customerPhone: '+919876543210',
    productId: 'P-101',
    productName: 'Surf Excel Easy Wash 1kg',
    qty: 1,
    unitPrice: 160,
    creditAmount: 160,
    type: RETURN_TYPE.RETURN_CREDIT,
    reason: 'Damaged in transit',
    comments: 'Packaging was torn and powder spilled inside outer bag.',
    status: RETURN_STATUS.REQUESTED,
    orderChannel: ORDER_CHANNEL.DELIVERY,
    requestedAt: new Date(Date.now() - 3600000 * 4).toISOString(),
    deliveryPersonSub: null,
    deliveryPersonName: null,
    rejectionReason: null,
    verificationNotes: null,
    evidencePhotoUrls: [],
    podPhotoUrl: null,
  },
  {
    id: 'RET-1009',
    orderId: 'ORD-8821',
    customerName: 'Rahul Verma',
    customerPhone: '+919876543210',
    productId: 'P-042',
    productName: 'Colgate MaxFresh 150g',
    qty: 2,
    unitPrice: 95,
    creditAmount: 190,
    type: RETURN_TYPE.EXCHANGE,
    reason: 'Wrong item delivered',
    reasonLabel: 'Wrong item delivered',
    comments: 'Ordered peppermint, received regular variant.',
    status: RETURN_STATUS.REQUESTED,
    orderChannel: ORDER_CHANNEL.DELIVERY,
    requestedAt: new Date(Date.now() - 3600000 * 3.5).toISOString(),
    deliveryPersonSub: null,
    deliveryPersonName: null,
    rejectionReason: null,
    verificationNotes: null,
    evidencePhotoUrls: [],
    podPhotoUrl: null,
  },
  {
    id: 'RET-1010',
    orderId: 'ORD-8821',
    customerName: 'Rahul Verma',
    customerPhone: '+919876543210',
    productId: 'P-088',
    productName: 'Lay\'s Classic Salted 52g',
    qty: 3,
    unitPrice: 20,
    creditAmount: 60,
    type: RETURN_TYPE.RETURN_CREDIT,
    reason: 'Damaged in transit',
    reasonLabel: 'Damaged in transit',
    comments: 'Two packs were crushed.',
    status: RETURN_STATUS.REQUESTED,
    orderChannel: ORDER_CHANNEL.DELIVERY,
    requestedAt: new Date(Date.now() - 3600000 * 3).toISOString(),
    deliveryPersonSub: null,
    deliveryPersonName: null,
    rejectionReason: null,
    verificationNotes: null,
    evidencePhotoUrls: [],
    podPhotoUrl: null,
  },
  {
    id: 'RET-1002',
    orderId: 'ORD-8819',
    customerName: 'Priya Sharma',
    customerPhone: '+919812345678',
    productId: 'P-204',
    productName: 'Amul Taaza Milk 1L',
    qty: 2,
    unitPrice: 70,
    creditAmount: 140,
    type: RETURN_TYPE.EXCHANGE,
    reason: 'Expired / Near Expiry',
    comments: 'Expiry date is tomorrow, need replacement pack.',
    status: RETURN_STATUS.PICKUP_SCHEDULED,
    orderChannel: ORDER_CHANNEL.SUBS,
    requestedAt: new Date(Date.now() - 3600000 * 20).toISOString(),
    deliveryPersonSub: 'rider-sub-001',
    deliveryPersonName: 'Vikram Singh',
    rejectionReason: null,
    verificationNotes: null,
    evidencePhotoUrls: [],
    podPhotoUrl: null,
  },
  {
    id: 'RET-1011',
    orderId: 'ORD-8819',
    customerName: 'Priya Sharma',
    customerPhone: '+919812345678',
    productId: 'P-301',
    productName: 'Mother Dairy Curd 400g',
    qty: 1,
    unitPrice: 35,
    creditAmount: 35,
    type: RETURN_TYPE.RETURN_CREDIT,
    reason: 'Expired / Near Expiry',
    reasonLabel: 'Expired / Near Expiry',
    comments: 'Curd was sour on arrival.',
    status: RETURN_STATUS.PICKUP_SCHEDULED,
    orderChannel: ORDER_CHANNEL.SUBS,
    requestedAt: new Date(Date.now() - 3600000 * 19).toISOString(),
    deliveryPersonSub: 'rider-sub-001',
    deliveryPersonName: 'Vikram Singh',
    rejectionReason: null,
    verificationNotes: null,
    evidencePhotoUrls: [],
    podPhotoUrl: null,
  },
  {
    id: 'RET-1003',
    orderId: 'ORD-8804',
    customerName: 'Ankit Gupta',
    customerPhone: '+919845098450',
    productId: 'P-311',
    productName: 'Tata Salt 1kg',
    qty: 3,
    unitPrice: 25,
    creditAmount: 75,
    type: RETURN_TYPE.RETURN_CREDIT,
    reason: 'Wrong item delivered',
    comments: 'Ordered iodized salt, received rock salt instead.',
    status: RETURN_STATUS.APPROVED,
    orderChannel: ORDER_CHANNEL.DELIVERY,
    requestedAt: new Date(Date.now() - 3600000 * 30).toISOString(),
    deliveryPersonSub: null,
    deliveryPersonName: null,
    rejectionReason: null,
    verificationNotes: null,
  },
  {
    id: 'RET-1004',
    orderId: 'ORD-8790',
    customerName: 'Sneha Reddy',
    customerPhone: '+919900112233',
    productId: 'P-118',
    productName: 'Fortune Sunflower Oil 1L',
    qty: 1,
    unitPrice: 145,
    creditAmount: 145,
    type: RETURN_TYPE.RETURN_CREDIT,
    reason: 'Leaking container',
    comments: 'Bottle cap was loose, oil leaked into the delivery bag.',
    status: RETURN_STATUS.PICKUP_COMPLETED,
    orderChannel: ORDER_CHANNEL.DELIVERY,
    requestedAt: new Date(Date.now() - 3600000 * 48).toISOString(),
    deliveryPersonSub: 'rider-sub-002',
    deliveryPersonName: 'Ramesh Kumar',
    rejectionReason: null,
    verificationNotes: null,
  },
  {
    id: 'RET-1005',
    orderId: 'ORD-8765',
    customerName: 'Karthik Iyer',
    customerPhone: '+919988776655',
    productId: 'P-092',
    productName: 'MDH Deggi Mirch 100g',
    qty: 2,
    unitPrice: 55,
    creditAmount: 110,
    type: RETURN_TYPE.EXCHANGE,
    reason: 'DAMAGED_EXPIRED',
    reasonLabel: 'Damaged / expired item',
    comments: 'Pouch was punctured, spice spilled inside the bag.',
    status: RETURN_STATUS.COMPLETED,
    orderChannel: ORDER_CHANNEL.DELIVERY,
    requestedAt: new Date(Date.now() - 3600000 * 72).toISOString(),
    deliveryPersonSub: 'rider-sub-001',
    deliveryPersonName: 'Vikram Singh',
    rejectionReason: null,
    verificationNotes: 'Item inspected, exchanged pack restocked after QC check.',
    evidencePhotoUrls: ['https://mock-evidence-bucket.s3.amazonaws.com/demo/mirch-punctured.jpg'],
    podPhotoUrl: 'https://mock-pod-bucket.s3.amazonaws.com/demo/ret-1005-pod.jpg',
    replacementOrderId: 'ORD-8912',
  },
  {
    id: 'RET-1008',
    orderId: 'ORD-8730',
    customerName: 'Meera Krishnan',
    customerPhone: '+919090909090',
    productId: 'P-118',
    productName: 'Fortune Sunflower Oil 1L',
    qty: 1,
    unitPrice: 180,
    creditAmount: 180,
    type: RETURN_TYPE.RETURN_CREDIT,
    reason: 'QUALITY_NOT_AS_EXPECTED',
    reasonLabel: 'Quality not as expected',
    comments: '',
    status: RETURN_STATUS.PICKUP_FAILED,
    orderChannel: ORDER_CHANNEL.DELIVERY,
    requestedAt: new Date(Date.now() - 3600000 * 30).toISOString(),
    deliveryPersonSub: 'rider-sub-002',
    deliveryPersonName: 'Ramesh Kumar',
    rejectionReason: 'Pickup failed: Customer unavailable',
    verificationNotes: null,
    evidencePhotoUrls: [],
    podPhotoUrl: null,
    replacementOrderId: null,
  },
  {
    id: 'RET-1006',
    orderId: 'ORD-8750',
    customerName: 'Divya Patel',
    customerPhone: '+919776655443',
    productId: 'P-067',
    productName: 'Britannia Good Day Cookies 200g',
    qty: 4,
    unitPrice: 30,
    creditAmount: 120,
    type: RETURN_TYPE.RETURN_CREDIT,
    reason: 'Changed mind',
    comments: 'Customer ordered extra by mistake, wants refund for 4 packs.',
    status: RETURN_STATUS.REJECTED,
    orderChannel: ORDER_CHANNEL.DELIVERY,
    requestedAt: new Date(Date.now() - 3600000 * 96).toISOString(),
    deliveryPersonSub: null,
    deliveryPersonName: null,
    rejectionReason: 'Return window expired and item is unopened/unused, not eligible for refund.',
    verificationNotes: null,
  },
  {
    id: 'RET-1007',
    orderId: 'SUB-DROP-2201',
    customerName: 'Mohammed Farhan',
    customerPhone: '+919123456780',
    productId: 'PACK-MILK',
    productName: 'Daily Milk Pack (1L)',
    qty: 1,
    unitPrice: 60,
    creditAmount: 60,
    type: RETURN_TYPE.RETURN_CREDIT,
    reason: 'Quality issue',
    comments: 'Milk smelled sour on subscription delivery.',
    status: RETURN_STATUS.REQUESTED,
    orderChannel: ORDER_CHANNEL.SUBS,
    requestedAt: new Date(Date.now() - 3600000 * 2).toISOString(),
    deliveryPersonSub: null,
    deliveryPersonName: null,
    rejectionReason: null,
    verificationNotes: null,
  },
];

function collectPhotoUrls(...candidates) {
  const out = [];
  const seen = new Set();
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    let list = candidate;
    if (typeof list === 'string') {
      const trimmed = list.trim();
      if (!trimmed) continue;
      if (trimmed.startsWith('[')) {
        try {
          list = JSON.parse(trimmed);
        } catch {
          list = [trimmed];
        }
      } else {
        list = [trimmed];
      }
    }
    if (!Array.isArray(list)) list = [list];
    for (const entry of list) {
      if (entry == null || entry === '') continue;
      const url = typeof entry === 'string'
        ? entry
        : (entry.url || entry.photo_url || entry.photoUrl || entry.pod_photo_url || null);
      if (!url || seen.has(url)) continue;
      seen.add(url);
      out.push(url);
    }
  }
  return out;
}

export function normalizeReturnRequest(rawInput) {
  if (!rawInput || typeof rawInput !== 'object') return null;
  const raw = rawInput.request || rawInput;
  const podPhotoUrls = collectPhotoUrls(
    raw.podPhotoUrls,
    raw.pod_photo_urls,
    raw.pickupPhotoUrls,
    raw.pickup_photo_urls,
    raw.podPhotos,
    raw.pod_photos,
    raw.podPhotoUrl,
    raw.pod_photo_url,
  );
  const qty = Number(raw.qty ?? raw.quantity ?? 1) || 1;
  const unitPrice = Number(raw.unitPrice ?? raw.unit_price ?? 0) || 0;
  const type = raw.type || RETURN_TYPE.RETURN_CREDIT;
  // Prefer credit_amount; if missing/0 on a credit return, derive from unit × qty.
  const creditRaw = raw.creditAmount ?? raw.credit_amount;
  let creditAmount = creditRaw != null && creditRaw !== '' ? Number(creditRaw) : NaN;
  if (!Number.isFinite(creditAmount)) creditAmount = 0;
  if (type === RETURN_TYPE.RETURN_CREDIT && creditAmount === 0 && unitPrice > 0) {
    creditAmount = unitPrice * qty;
  }
  return {
    id: raw.id || raw.request_id || '',
    orderId: raw.orderId || raw.order_id || '',
    customerName: raw.customerName || raw.customer_name || 'Customer',
    customerPhone: raw.customerPhone || raw.customer_phone || '',
    productId: raw.productId || raw.product_id || '',
    productName: raw.productName || raw.product_name || 'Product',
    qty,
    unitPrice,
    creditAmount: type === RETURN_TYPE.EXCHANGE ? 0 : creditAmount,
    type,
    reason: raw.reason || 'Item issue',
    reasonLabel: raw.reasonLabel || raw.reason_label || raw.reason || 'Item issue',
    comments: raw.comments || '',
    status: raw.status || RETURN_STATUS.REQUESTED,
    orderChannel: normalizeOrderChannel(raw),
    requestedAt: raw.requestedAt || raw.requested_at || new Date().toISOString(),
    deliveryPersonSub: raw.deliveryPersonSub || raw.delivery_person_sub || null,
    deliveryPersonName: raw.deliveryPersonName || raw.delivery_person_name || null,
    rejectionReason: raw.rejectionReason || raw.rejection_reason || null,
    verificationNotes: raw.verificationNotes || raw.verification_notes || null,
    evidencePhotoUrls: collectPhotoUrls(raw.evidencePhotoUrls, raw.evidence_photo_urls),
    podPhotoUrl: podPhotoUrls[0] || null,
    podPhotoUrls,
    replacementOrderId: raw.replacementOrderId || raw.replacement_order_id || null,
    resolution: raw.resolution || null,
    restocked: raw.restocked == null ? null : !!raw.restocked,
  };
}

function applyReturnFilters(list, { statusFilter = null, channelFilter = null } = {}) {
  const isAllStatus = !statusFilter || statusFilter === 'ALL';
  const isAllChannel = !channelFilter || channelFilter === 'ALL';
  let result = list;
  if (!isAllStatus) {
    result = result.filter((r) => r.status === statusFilter);
  }
  if (!isAllChannel) {
    result = result.filter((r) => r.orderChannel === channelFilter);
  }
  return result;
}

export async function listReturnRequests(statusFilter = null, token = null, { channel = null } = {}) {
  const isAllStatus = !statusFilter || statusFilter === 'ALL';
  try {
    const params = new URLSearchParams();
    if (!isAllStatus) params.set('status', statusFilter);
    // Handoff: no ?channel= on list — filter order_channel in the client.
    const qs = params.toString() ? `?${params.toString()}` : '';
    const raw = await apiFetch(`/store/returns${qs}`, { token });
    return applyReturnFilters(
      (raw?.requests || []).map(normalizeReturnRequest).filter(Boolean),
      { statusFilter, channelFilter: channel },
    );
  } catch (err) {
    // Auth failures must surface — falling back to mock after a 401 refresh
    // made the UI look successful while the session token kept rotating.
    if (err?.status === 401 || err?.status === 403) throw err;
    // Fallback to mock data for demo/offline
    return applyReturnFilters(
      mockReturnRequests.map(normalizeReturnRequest).filter(Boolean),
      { statusFilter, channelFilter: channel },
    );
  }
}

export async function getReturnRequest(requestId, token = null) {
  try {
    const raw = await apiFetch(`/store/returns/${encodeURIComponent(requestId)}`, { token });
    return normalizeReturnRequest(raw);
  } catch (err) {
    const item = mockReturnRequests.find((r) => r.id === requestId);
    if (!item) throw new Error('Return request not found');
    return normalizeReturnRequest(item);
  }
}

export async function approveReturnRequest(requestId, { notes } = {}, token = null) {
  try {
    const raw = await apiFetch(`/store/returns/${encodeURIComponent(requestId)}/approve`, {
      method: 'POST',
      token,
      body: { notes },
    });
    return normalizeReturnRequest(raw);
  } catch (err) {
    const index = mockReturnRequests.findIndex((r) => r.id === requestId);
    if (index !== -1) {
      mockReturnRequests[index] = {
        ...mockReturnRequests[index],
        status: RETURN_STATUS.APPROVED,
      };
      return normalizeReturnRequest(mockReturnRequests[index]);
    }
    throw err;
  }
}

/** Customer discards item; credits wallet when photos are enough. No pickup. */
export async function disposeReturnRequest(requestId, { notes } = {}, token = null) {
  try {
    const raw = await apiFetch(`/store/returns/${encodeURIComponent(requestId)}/dispose`, {
      method: 'POST',
      token,
      body: { ...(notes ? { notes } : {}) },
    });
    return normalizeReturnRequest(raw);
  } catch (err) {
    const code = err?.code || err?.message;
    if (RETURN_ERRORS[code]) {
      err.message = RETURN_ERRORS[code];
    }
    const index = mockReturnRequests.findIndex((r) => r.id === requestId);
    if (index !== -1) {
      const item = mockReturnRequests[index];
      const photos = item.evidencePhotoUrls || item.evidence_photo_urls || [];
      if (item.type !== RETURN_TYPE.RETURN_CREDIT) {
        throw Object.assign(new Error(RETURN_ERRORS.dispose_credit_only), { code: 'dispose_credit_only' });
      }
      if (!photos.length) {
        throw Object.assign(new Error(RETURN_ERRORS.evidence_required), { code: 'evidence_required' });
      }
      mockReturnRequests[index] = {
        ...item,
        status: RETURN_STATUS.COMPLETED,
        resolution: RETURN_RESOLUTION.CUSTOMER_DISPOSE,
        restocked: false,
        verificationNotes: notes || item.verificationNotes,
      };
      return normalizeReturnRequest(mockReturnRequests[index]);
    }
    throw err;
  }
}

export async function rejectReturnRequest(requestId, reason, token = null) {
  try {
    const raw = await apiFetch(`/store/returns/${encodeURIComponent(requestId)}/reject`, {
      method: 'POST',
      token,
      body: { reason },
    });
    return normalizeReturnRequest(raw);
  } catch (err) {
    const index = mockReturnRequests.findIndex((r) => r.id === requestId);
    if (index !== -1) {
      mockReturnRequests[index] = {
        ...mockReturnRequests[index],
        status: RETURN_STATUS.REJECTED,
        rejectionReason: reason,
      };
      return normalizeReturnRequest(mockReturnRequests[index]);
    }
    throw err;
  }
}

export async function assignReturnRider(requestId, deliveryPersonSub, deliveryPersonName = 'Rider', token = null) {
  try {
    const raw = await apiFetch(`/store/returns/${encodeURIComponent(requestId)}/assign`, {
      method: 'POST',
      token,
      body: { delivery_person_sub: deliveryPersonSub },
    });
    return normalizeReturnRequest(raw);
  } catch (err) {
    const index = mockReturnRequests.findIndex((r) => r.id === requestId);
    if (index !== -1) {
      mockReturnRequests[index] = {
        ...mockReturnRequests[index],
        status: RETURN_STATUS.PICKUP_SCHEDULED,
        deliveryPersonSub,
        deliveryPersonName,
      };
      return normalizeReturnRequest(mockReturnRequests[index]);
    }
    throw err;
  }
}

export async function completeStoreVerification(requestId, { restock = true, notes = '' } = {}, token = null) {
  try {
    const raw = await apiFetch(`/store/returns/${encodeURIComponent(requestId)}/verify`, {
      method: 'POST',
      token,
      body: { restock, notes },
    });
    return normalizeReturnRequest(raw);
  } catch (err) {
    const index = mockReturnRequests.findIndex((r) => r.id === requestId);
    if (index !== -1) {
      mockReturnRequests[index] = {
        ...mockReturnRequests[index],
        status: RETURN_STATUS.COMPLETED,
        verificationNotes: notes,
      };
      return normalizeReturnRequest(mockReturnRequests[index]);
    }
    throw err;
  }
}

// Driver functions for Return Pickups
export async function listDriverReturnTasks(token = null) {
  try {
    const raw = await apiFetch('/delivery/returns', { token });
    return (raw?.requests || []).map(normalizeReturnRequest);
  } catch (err) {
    return mockReturnRequests
      .filter((r) => r.status === RETURN_STATUS.PICKUP_SCHEDULED)
      .map(normalizeReturnRequest);
  }
}

/** Resolve a driver pickup by id from GET /delivery/returns list (no detail endpoint). */
export async function getDriverReturnRequest(requestId, token = null) {
  const tasks = await listDriverReturnTasks(token);
  const fromList = tasks.find((r) => r.id === requestId);
  if (fromList) return fromList;

  try {
    return await getReturnRequest(requestId, token);
  } catch {
    throw new Error('not_found');
  }
}

/** All scheduled pickup tasks for one order assigned to the driver. */
export async function getDriverReturnTasksByOrder(orderId, token = null) {
  const tasks = await listDriverReturnTasks(token);
  return tasks
    .filter((r) => r.orderId === orderId)
    .sort((a, b) => a.id.localeCompare(b.id));
}

export async function listReturnRequestsByOrder(orderId, token = null, { status = null } = {}) {
  const all = await listReturnRequests('ALL', token);
  return all.filter((r) => r.orderId === orderId && (!status || r.status === status));
}

export async function confirmReturnPickup(requestId, { photoKey, photoKeys, notes } = {}, token = null) {
  const keys = (photoKeys?.length ? photoKeys : (photoKey ? [photoKey] : [])).filter(Boolean);
  if (keys.length === 0) {
    throw new Error('pod_photo_required');
  }
  try {
    const raw = await apiFetch(`/delivery/returns/${encodeURIComponent(requestId)}/confirm-pickup`, {
      method: 'POST',
      token,
      body: {
        pod_photo_s3_key: keys[0],
        pod_photo_s3_keys: keys,
        notes,
      },
    });
    return normalizeReturnRequest(raw);
  } catch (err) {
    const index = mockReturnRequests.findIndex((r) => r.id === requestId);
    if (index !== -1) {
      const podPhotoUrls = keys.map((key) => `https://mock-pod-bucket.s3.amazonaws.com/${key}`);
      mockReturnRequests[index] = {
        ...mockReturnRequests[index],
        status: RETURN_STATUS.PICKUP_COMPLETED,
        verificationNotes: notes,
        podPhotoUrl: podPhotoUrls[0],
        podPhotoUrls,
      };
      return normalizeReturnRequest(mockReturnRequests[index]);
    }
    throw err;
  }
}

export async function failReturnPickup(requestId, reason, token = null) {
  try {
    const raw = await apiFetch(`/delivery/returns/${encodeURIComponent(requestId)}/fail-pickup`, {
      method: 'POST',
      token,
      body: { reason },
    });
    return normalizeReturnRequest(raw);
  } catch (err) {
    const index = mockReturnRequests.findIndex((r) => r.id === requestId);
    if (index !== -1) {
      mockReturnRequests[index] = {
        ...mockReturnRequests[index],
        status: RETURN_STATUS.PICKUP_FAILED,
        rejectionReason: `Pickup failed: ${reason}`,
      };
      return normalizeReturnRequest(mockReturnRequests[index]);
    }
    throw err;
  }
}

/**
 * Return / credit reason catalog.
 * Grocery: omit channel (or channel=DELIVERY).
 * Subscription deliveries: channel=SUBS (or pass order_id for a SUBS order).
 */
export async function getReturnReasons(token, { channel, orderId } = {}) {
  const params = new URLSearchParams();
  if (orderId) params.set('order_id', orderId);
  else if (channel) params.set('channel', channel);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const raw = await apiFetch(`/config/return-reasons${qs}`, { token });
  return {
    channel: raw?.channel || channel || 'DELIVERY',
    reasons: (raw?.reasons || []).map((r) => ({
      reason: r.reason,
      label: r.label || r.reason,
      description: r.description || '',
      evidenceRequired: !!r.evidence_required,
    })),
  };
}
